// The in-memory API's Setup › Model policy (MODEL-POLICY; services/api/src/routes/model-policy.ts)
// with the real route's rules: managers only (a client viewer is 403); the policy lives in
// `company.settings.model_policy`, so `PATCH /v1/companies/{co}` and this PUT see one value; every
// model it names is checked against the fake gateway's list and regions (`policyProblems`) before
// anything is stored; an unchanged PUT changes nothing. The BYO policy is the subscriptions twin's
// (its `subscriptionPolicy.get` answer), the overrides are the deploys twin's role-to-model maps.
import type { OperationInput, OperationOutput, RouteId } from "@companyos/sdk";
import type { Handler, Principal } from "./fake";
import { json, notFound, problem } from "./fake";
import type { FakeDe } from "./fake-data";
import { FAKE_GATEWAY_MODELS } from "./fake-deploys";

type View = OperationOutput<"modelPolicy.get">;
type Policy = View["policy"];
type Override = OperationOutput<"digitalEmployees.templateOverride">["override"] & {};
type Problemish = { path: string; code: string; message: string };

/** The task kinds the page offers (`MODEL_TASK_KINDS` of @companyos/schema; a parity test pins it). */
export const FAKE_TASK_KINDS = ["lead", "draft", "review", "verify"] as const;
/** Where the fake gateway serves each model (`LITELLM_MODEL_REGIONS`). */
export const FAKE_GATEWAY_REGIONS: Record<(typeof FAKE_GATEWAY_MODELS)[number], string[]> = {
  "gpt-4o": ["us"],
  "claude-sonnet": ["eu", "us"],
  "claude-haiku": ["eu", "us"],
};
const ALIAS = /^[A-Za-z0-9][A-Za-z0-9._:/@-]{0,127}$/;
const REGION = /^[a-z]{2,8}$/;
const KIND = /^[a-z][a-z0-9_]{0,63}$/;
const FIELDS = ["residency", "allowed_models", "default_model", "role_defaults"] as const;

function invalid(errors: Problemish[]): Response {
  return new Response(
    JSON.stringify({
      type: "https://companyos.app/errors/validation_failed",
      title: "Validation failed",
      status: 422,
      code: "validation_failed",
      request_id: `fake-${Math.random().toString(36).slice(2, 10)}`,
      detail: errors.map((e) => `${e.path}: ${e.message}`).join("; "),
      errors,
    }),
    { status: 422, headers: { "content-type": "application/problem+json" } },
  );
}

/** routing.ts `allowedModels`: the gateway's list narrowed by the allow-list and residency. */
export function fakeRoutable(policy: Policy): string[] {
  const allow = policy.allowed_models ? new Set(policy.allowed_models) : null;
  return FAKE_GATEWAY_MODELS.filter(
    (m) =>
      (allow === null || allow.has(m)) &&
      (!policy.residency || FAKE_GATEWAY_REGIONS[m].includes(policy.residency)),
  );
}

/** routing.ts `policyProblems`, against the fake gateway (it always lists its models). */
export function fakePolicyProblems(policy: Policy): Problemish[] {
  const out: Problemish[] = [];
  (policy.allowed_models ?? []).forEach((m, i) => {
    if (!(FAKE_GATEWAY_MODELS as readonly string[]).includes(m))
      out.push({
        path: `allowed_models.${i}`,
        code: "model_not_on_gateway",
        message: `the gateway does not serve ${m}`,
      });
  });
  const routable = new Set(fakeRoutable(policy));
  if (policy.residency && routable.size === 0)
    out.push({
      path: "residency",
      code: "residency_unsatisfiable",
      message: `no allowed model is served in ${policy.residency}`,
    });
  const pref = (path: string, m: string | undefined) => {
    if (m !== undefined && !routable.has(m))
      out.push({
        path,
        code: "model_not_allowed",
        message: `${m} is not among the models this policy allows`,
      });
  };
  pref("default_model", policy.default_model);
  for (const [k, m] of Object.entries(policy.role_defaults ?? {}).sort(([a], [b]) =>
    a < b ? -1 : 1,
  ))
    pref(`role_defaults.${k}`, m);
  return out;
}

/** The body's shape (`ModelPolicyPut`), or what is wrong with it. */
function parse(body: unknown): Policy | Problemish[] {
  const b = (body ?? {}) as Record<string, unknown>;
  const errs: Problemish[] = [];
  const bad = (path: string, message: string) => errs.push({ path, code: "invalid", message });
  for (const k of Object.keys(b))
    if (!(FIELDS as readonly string[]).includes(k)) bad(k, "unknown field");
  for (const k of FIELDS) if (!(k in b)) bad(k, "required");
  const { residency, allowed_models, default_model, role_defaults } = b;
  if (residency !== null && (typeof residency !== "string" || !REGION.test(residency)))
    bad("residency", "a lowercase region code such as eu or us");
  if (
    allowed_models !== null &&
    (!Array.isArray(allowed_models) ||
      allowed_models.length < 1 ||
      allowed_models.length > 50 ||
      allowed_models.some((m) => typeof m !== "string" || !ALIAS.test(m)))
  )
    bad("allowed_models", "1 to 50 gateway model aliases, or null");
  if (default_model !== null && (typeof default_model !== "string" || !ALIAS.test(default_model)))
    bad("default_model", "a gateway model alias");
  const kinds =
    role_defaults && typeof role_defaults === "object" && !Array.isArray(role_defaults)
      ? Object.entries(role_defaults as Record<string, unknown>)
      : null;
  if (
    !kinds ||
    kinds.length > 20 ||
    kinds.some(([k, m]) => !KIND.test(k) || typeof m !== "string" || !ALIAS.test(m))
  )
    bad("role_defaults", "task kind → gateway model alias, at most 20");
  if (errs.length) return errs;
  const sorted = (kinds ?? []).sort(([a], [b]) => (a < b ? -1 : 1)) as [string, string][];
  return {
    ...(residency !== null ? { residency: residency as string } : {}),
    ...(allowed_models !== null
      ? { allowed_models: [...new Set(allowed_models as string[])].sort() }
      : {}),
    ...(default_model !== null ? { default_model: default_model as string } : {}),
    ...(sorted.length ? { role_defaults: Object.fromEntries(sorted) } : {}),
  };
}

type EffortView = OperationOutput<"runEffort.get">;
type EffortSettings = OperationInput<"runEffort.put">["body"];
type EffortTier = EffortView["tiers"][number]["tier"];
type TierSpec = { model: string; reasoning: "off" | "low" | "medium" | "high" };
const EFFORT_TIERS: readonly EffortTier[] = ["low", "medium", "high", "vision"];
/** The built-in tiers (services/api/src/domain/runs/effort-tiers.ts; founder 2026-10-30). */
export const EFFORT_DEFAULTS: Record<EffortTier, TierSpec> = {
  low: { model: "deepseek-flash", reasoning: "off" },
  medium: { model: "deepseek-flash", reasoning: "medium" },
  high: { model: "claude-haiku-5-5", reasoning: "high" },
  vision: { model: "claude-haiku-5-5", reasoning: "medium" },
};
const REASONING = new Set(["off", "low", "medium", "high"]);
const REQUESTS = new Set(["auto", "low", "medium", "high"]);

/** routes/run-effort.ts `normalizeEffort` over `RunEffortSettings`, or what is wrong with it. */
function parseEffort(body: unknown): EffortSettings | Problemish[] {
  const b = (body ?? {}) as Record<string, unknown>;
  const errs: Problemish[] = [];
  const bad = (path: string, message: string) => errs.push({ path, code: "invalid", message });
  for (const k of Object.keys(b))
    if (!["tiers", "refine_with_classifier", "for_agent"].includes(k)) bad(k, "unknown field");
  const tiersOf = (v: unknown, path: string): Partial<Record<EffortTier, TierSpec>> => {
    if (v === undefined) return {};
    if (!v || typeof v !== "object" || Array.isArray(v)) {
      bad(path, "an object");
      return {};
    }
    const out: Partial<Record<EffortTier, TierSpec>> = {};
    for (const [t, spec] of Object.entries(v as Record<string, unknown>)) {
      const s = (spec ?? {}) as Record<string, unknown>;
      if (!(EFFORT_TIERS as readonly string[]).includes(t)) bad(`${path}.${t}`, "unknown tier");
      else if (typeof s.model !== "string" || !ALIAS.test(s.model))
        bad(`${path}.${t}.model`, "a gateway model alias");
      else if (!REASONING.has(s.reasoning as string))
        bad(`${path}.${t}.reasoning`, "off, low, medium or high");
      else
        out[t as EffortTier] = { model: s.model, reasoning: s.reasoning as TierSpec["reasoning"] };
    }
    return out;
  };
  const tiers = tiersOf(b.tiers, "tiers");
  const forAgent: Record<string, { default?: "low" | "medium" | "high"; tiers?: object }> = {};
  if (b.for_agent !== undefined) {
    if (!b.for_agent || typeof b.for_agent !== "object") bad("for_agent", "an object");
    else
      for (const [id, o] of Object.entries(b.for_agent as Record<string, unknown>)) {
        const x = (o ?? {}) as Record<string, unknown>;
        if (x.default !== undefined && !REQUESTS.has(x.default as string))
          bad(`for_agent.${id}.default`, "auto, low, medium or high");
        const own = tiersOf(x.tiers, `for_agent.${id}.tiers`);
        const v = {
          ...(x.default && x.default !== "auto"
            ? { default: x.default as "low" | "medium" | "high" }
            : {}),
          ...(Object.keys(own).length ? { tiers: own } : {}),
        };
        if (Object.keys(v).length) forAgent[id.toLowerCase()] = v;
      }
  }
  if (errs.length) return errs;
  return {
    ...(Object.keys(tiers).length ? { tiers } : {}),
    ...(b.refine_with_classifier === true ? { refine_with_classifier: true } : {}),
    ...(Object.keys(forAgent).length ? { for_agent: forAgent } : {}),
  } as EffortSettings;
}

const canon = (v: unknown) =>
  v && typeof v === "object" && !Array.isArray(v)
    ? JSON.stringify(Object.entries(v).sort(([a], [b]) => (a < b ? -1 : 1)))
    : JSON.stringify(v ?? null);

export function createModelPolicyHandlers(deps: {
  now: () => Date;
  desOf: (companyId: string) => FakeDe[];
  overrides: () => readonly Override[];
  /** The subscriptions twin's `subscriptionPolicy.get`, for the BYO answer. */
  byo: (p: Principal) => View["byo"];
}): Partial<Record<RouteId, Handler>> {
  function view(p: Principal): View {
    const policy = (p.company.settings.model_policy ?? {}) as Policy;
    const routable = new Set(fakeRoutable(policy));
    const names = new Map(deps.desOf(p.company.id).map((d) => [d.membership_id, d.display_name]));
    const data = deps
      .overrides()
      .filter(
        (o) => o.company_id === p.company.id && o.role_model_map && names.has(o.de_membership_id),
      )
      .flatMap((o) =>
        Object.entries(o.role_model_map ?? {})
          .sort(([a], [b]) => (a < b ? -1 : 1))
          .map(([role, e]) => ({
            agent_kind: "digital_employee" as const,
            membership_id: o.de_membership_id,
            name: names.get(o.de_membership_id) ?? "",
            source: "role_model_map" as const,
            role,
            model: e.model,
            allowed: routable.has(e.model),
          })),
      )
      .sort((a, b) => (a.name === b.name ? (a.role < b.role ? -1 : 1) : a.name < b.name ? -1 : 1));
    const extra = Object.keys(policy.role_defaults ?? {})
      .filter((k) => !(FAKE_TASK_KINDS as readonly string[]).includes(k))
      .sort();
    return {
      policy,
      gateway: {
        listed: true,
        models: FAKE_GATEWAY_MODELS.map((alias) => ({
          alias,
          regions: FAKE_GATEWAY_REGIONS[alias],
          allowed: routable.has(alias),
        })),
      },
      task_kinds: [...FAKE_TASK_KINDS, ...extra],
      overrides: { data: data.slice(0, 200), truncated: data.length > 200 },
      byo: deps.byo(p),
    };
  }

  // RUN-EFFORT: the tier → model map in `company.settings.run_effort` (routes/run-effort.ts).
  function effortView(p: Principal): EffortView {
    const s = (p.company.settings.run_effort ?? {}) as EffortSettings;
    const routable = new Set(fakeRoutable((p.company.settings.model_policy ?? {}) as Policy));
    const agents = deps
      .desOf(p.company.id)
      .map((d) => ({
        id: d.membership_id,
        kind: "digital_employee" as const,
        name: d.display_name,
        override: s.for_agent?.[d.membership_id] ?? null,
      }))
      .sort((a, b) => (a.name < b.name ? -1 : a.name > b.name ? 1 : 0));
    return {
      tiers: EFFORT_TIERS.map((t) => {
        const spec = s.tiers?.[t] ?? EFFORT_DEFAULTS[t];
        return {
          tier: t,
          ...spec,
          custom: s.tiers?.[t] !== undefined,
          routable: routable.has(spec.model),
        };
      }),
      defaults: EFFORT_TIERS.map((t) => ({ tier: t, ...EFFORT_DEFAULTS[t] })),
      refine_with_classifier: s.refine_with_classifier === true,
      agents,
      truncated: false,
    };
  }

  return {
    "runEffort.get": ({ principal }) => {
      if (!principal) return notFound();
      if (!principal.manager) return problem(403, "forbidden", "Forbidden");
      return json(effortView(principal));
    },
    "runEffort.put": ({ principal, body }) => {
      if (!principal) return notFound();
      if (!principal.manager) return problem(403, "forbidden", "Forbidden");
      const next = parseEffort(body);
      if (Array.isArray(next)) return invalid(next);
      const des = new Set(deps.desOf(principal.company.id).map((d) => d.membership_id));
      const unknown = Object.keys(next.for_agent ?? {}).filter((id) => !des.has(id));
      if (unknown.length)
        return invalid(
          unknown.map((id) => ({
            path: `for_agent.${id}`,
            code: "unknown_agent",
            message: "not a DE or a helper of this company",
          })),
        );
      const before = principal.company.settings.run_effort ?? {};
      if (JSON.stringify(before) !== JSON.stringify(next)) {
        const { run_effort: _old, ...rest } = principal.company.settings;
        Object.assign(principal.company, {
          settings: Object.keys(next).length ? { ...rest, run_effort: next } : rest,
          updated_at: deps.now().toISOString(),
        });
      }
      return json(effortView(principal));
    },
    "modelPolicy.get": ({ principal }) => {
      if (!principal) return notFound();
      if (!principal.manager) return problem(403, "forbidden", "Forbidden");
      return json(view(principal));
    },
    "modelPolicy.put": ({ principal, body }) => {
      if (!principal) return notFound();
      if (!principal.manager) return problem(403, "forbidden", "Forbidden");
      const next = parse(body);
      if (Array.isArray(next)) return invalid(next);
      const problems = fakePolicyProblems(next);
      if (problems.length) return invalid(problems);
      const before = (principal.company.settings.model_policy ?? {}) as Policy;
      if (FIELDS.some((f) => canon(before[f]) !== canon(next[f]))) {
        const { model_policy: _old, ...rest } = principal.company.settings;
        Object.assign(principal.company, {
          settings: Object.keys(next).length ? { ...rest, model_policy: next } : rest,
          updated_at: deps.now().toISOString(),
        });
      }
      return json(view(principal));
    },
  };
}
